import React from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import './Home.css';
import HomeImageSlider from '../components/HomeImageSlider';
import { 
  Camera, 
  ShieldCheck, 
  Webcam, 
  Wrench, 
  CheckCircle,
  Star,
  Phone,
  Clock,
  Users
} from 'phosphor-react';

const Home = () => {
  const navigate = useNavigate();

  const services = [
    {
      icon: <Camera size={48} />,
      title: "Videovigilância",
      description: "Monitorização 24/7 com câmaras de alta definição e acesso remoto via smartphone."
    },
    {
      icon: <ShieldCheck size={48} />,
      title: "Sistemas de Alarme",
      description: "Proteção total com sensores inteligentes e resposta imediata a intrusões."
    },
    {
      icon: <Webcam size={48} />,
      title: "Videoporteiros",
      description: "Controlo de acesso seguro com identificação visual e comunicação bidireccional."
    },
    {
      icon: <Wrench size={48} />,
      title: "Instalação & Manutenção",
      description: "Suporte técnico especializado e manutenção preventiva dos seus sistemas."
    }
  ];

  const benefits = [
    {
      icon: <Star size={24} />,
      title: "15+ anos de experiência",
      description: "Especialistas em segurança com vasta experiência no mercado"
    },
    {
      icon: <Wrench size={24} />,
      title: "Instalação profissional",
      description: "Técnicos certificados e equipamentos de qualidade superior"
    },
    {
      icon: <Phone size={24} />,
      title: "Suporte técnico 24/7",
      description: "Assistência técnica disponível sempre que precisar"
    },
    {
      icon: <CheckCircle size={24} />,
      title: "Garantia de qualidade",
      description: "Produtos certificados com garantia estendida"
    },
    {
      icon: <Users size={24} />,
      title: "Orçamentos gratuitos",
      description: "Análise das suas necessidades sem qualquer custo"
    },
    {
      icon: <Clock size={24} />,
      title: "Resposta rápida",
      description: "Instalação e manutenção com prazos cumpridos"
    }
  ];

  return (
    <div className="home-container">
      <SEOHead
        title="MercadoTV - Sistemas de Videovigilância e Segurança em Portugal"
        description="Especialistas em sistemas de videovigilância, alarmes Ajax e videoporteiros em Portugal. Instalação profissional sem mensalidades. Orçamentos gratuitos e personalizados para sua segurança."
        keywords="videovigilância portugal, alarmes ajax, videoporteiros, câmaras segurança lisboa porto, instalação cctv, sistemas segurança residencial, alarmes loja, videovigilância ip, segurança sem fios, orçamento segurança, mercadotv"
        canonicalUrl="https://mercadotv.pt/"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": "MercadoTV",
          "description": "Especialistas em sistemas de videovigilância, alarmes Ajax e videoporteiros em Portugal",
          "url": "https://mercadotv.pt",
          "logo": "https://mercadotv.pt/logo.png",
          "telephone": "256418202",
          "email": "mlptelecomunicacoes@gmail.com",
          "address": {
            "@type": "PostalAddress",
            "streetAddress": "Rua do Mercado nº298",
            "addressLocality": "Vale de Cambra",
            "postalCode": "3730-000",
            "addressRegion": "Aveiro",
            "addressCountry": "PT"
          },
          "hasOfferCatalog": {
            "@type": "OfferCatalog",
            "name": "Sistemas de Segurança",
            "itemListElement": [
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Sistemas de Videovigilância",
                  "description": "Monitorização 24/7 com câmaras de alta definição"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Alarmes Ajax",
                  "description": "Proteção total com sensores inteligentes"
                }
              },
              {
                "@type": "Offer",
                "itemOffered": {
                  "@type": "Service",
                  "name": "Videoporteiros",
                  "description": "Controlo de acesso com vídeo"
                }
              }
            ]
          }
        }}
      />
      
      {/* Hero Slider Section */}
      <section className="hero-slider-section">
        <HomeImageSlider />
      </section>

      {/* Services Section */}
      <section className="services-section" itemScope itemType="https://schema.org/Service">
        <div className="container">
          <header className="section-header">
            <h2>Os Nossos Serviços</h2>
            <p>Soluções completas em segurança para todas as suas necessidades</p>
          </header>
          <div className="services-grid" role="list">
            {services.map((service, index) => (
              <article key={`service-${index}`} className="service-card" role="listitem" itemScope itemType="https://schema.org/Service">
                <div className="service-icon" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 itemProp="name">{service.title}</h3>
                <p itemProp="description">{service.description}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="benefits-section">
        <div className="container">
          <div className="section-header">
            <h2>Porquê Escolher-nos?</h2>
            <p>A sua segurança é a nossa prioridade. Conheça os nossos diferenciais</p>
          </div>
          <div className="benefits-grid">
            {benefits.map((benefit, index) => (
              <div key={`benefit-${index}`} className="benefit-card">
                <div className="benefit-icon">
                  {benefit.icon}
                </div>
                <div className="benefit-content">
                  <h3>{benefit.title}</h3>
                  <p>{benefit.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>Pronto para proteger o que mais importa?</h2>
            <p>Solicite um orçamento gratuito e descubra a solução ideal para si</p>
            <div className="cta-buttons">
              <button 
                className="btn btn-primary"
                onClick={() => navigate('/orcamento')}
              >
                Solicitar Orçamento
              </button>
              <button 
                className="btn btn-outline"
                onClick={() => navigate('/contactos')}
              >
                Falar Connosco
              </button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
